// E5 — apply external git/merge evidence to the latest session outcome.

import { apiFetch } from './api.js';
import { hasElectronBridge } from './electronBridge.js';
import { recordOutcome } from '../hooks/useOutcomes.js';
import { deriveExternalSignal } from './externalSignal.js';
import { formatSelfImproveError } from './formatSelfImproveError.js';

function isMissingOutcomeError(msg) {
  return /no pending outcome/i.test(msg || '') || /^not found$/i.test(String(msg || '').trim());
}

export async function finalizeExternalOutcome(sessionId, evidence = {}) {
  if (!sessionId) return { ok: false, error: 'sessionId required' };
  try {
    const res = await apiFetch(`/api/code-ide/sessions/${sessionId}/outcomes/finalize-external`, {
      method: 'POST',
      body: { evidence },
    });
    if (res.ok) return { ok: true, data: res.data };
    return {
      ok: false,
      error: formatSelfImproveError(res.error || `HTTP ${res.status}`, { status: res.status }),
      status: res.status,
    };
  } catch (e) {
    return { ok: false, error: formatSelfImproveError(e?.message || String(e)) };
  }
}

/** Fallback when finalize-external is not deployed: POST outcome + PATCH signal. */
async function applySignalViaLegacyEndpoints(sessionId, evidence, model_id) {
  const recorded = await recordOutcome({
    session_id: sessionId,
    model_id: model_id || 'unknown',
    task_type: 'fix',
    green_pass: 1,
  });
  if (!recorded.ok) {
    return { ok: false, error: formatSelfImproveError(recorded.error || 'Failed to record outcome') };
  }
  const signal = deriveExternalSignal(evidence);
  const patch = await apiFetch(`/api/code-ide/outcomes/${recorded.id}/signal`, {
    method: 'PATCH',
    body: signal,
  });
  if (!patch.ok) {
    return { ok: false, error: formatSelfImproveError(patch.error || `HTTP ${patch.status}`, { status: patch.status }) };
  }
  return { ok: true, data: { outcome_id: recorded.id, ...patch.data, signal } };
}

/**
 * Read the REMOTE PR merge state (the half local git can never see — a remote
 * squash-merge leaves the local worktree HEAD untouched) and, when a HUMAN
 * merged it, finalize the session outcome with the STRONG external signal.
 *
 * The merge evidence ({merged, mergedBy, autoMerged}) is handed to the existing
 * finalize path, where the server runs deriveExternalSignal (isHumanMerge
 * excludes web-flow/bots/auto-merge, unchanged) and re-targets the latest row
 * whose human_merged hasn't landed yet. We do NOT finalize an un-merged PR —
 * that would write a premature null signal.
 *
 * Deps are injectable for tests; production wires apiFetch + finalizeExternalOutcome.
 *
 * @param {string} sessionId
 * @param {{ pr_number?: number, owner?: string, repo?: string }} pr
 * @param {{ apiFetch?: typeof apiFetch, finalize?: typeof finalizeExternalOutcome }} [deps]
 */
export async function syncMergeSignalFromPR(sessionId, { pr_number, owner, repo } = {}, deps = {}) {
  const fetchImpl = deps.apiFetch || apiFetch;
  const finalize = deps.finalize || finalizeExternalOutcome;

  if (!sessionId || !Number.isInteger(pr_number) || pr_number <= 0 || !owner || !repo) {
    return { ok: false, skipped: true };
  }
  try {
    const q = `pr_number=${pr_number}&owner=${encodeURIComponent(owner)}&repo=${encodeURIComponent(repo)}`;
    const res = await fetchImpl(`/api/code-ide/sessions/${sessionId}/pr-merge-status?${q}`);
    if (!res?.ok) {
      return { ok: false, error: formatSelfImproveError(res?.error || `HTTP ${res?.status}`, { status: res?.status }) };
    }
    const { merged, mergedBy, autoMerged } = res.data || {};
    // Only finalize once the PR is actually merged — an open PR has no signal yet.
    if (!merged) return { ok: false, merged: false };
    const result = await finalize(sessionId, { merged: !!merged, mergedBy: mergedBy ?? null, autoMerged: !!autoMerged });
    return { ...result, merged: true };
  } catch (e) {
    return { ok: false, error: formatSelfImproveError(e?.message || String(e)) };
  }
}

/** Best-effort: read merge evidence from git HEAD and finalize the session outcome. */
export async function syncExternalSignalFromGit(sessionId, root, { model_id } = {}) {
  if (!sessionId || !root || !hasElectronBridge() || !window.apolo?.git?.commitEvidence) {
    return { ok: false, error: formatSelfImproveError('git bridge unavailable') };
  }
  try {
    const ev = await window.apolo.git.commitEvidence({ root });
    if (!ev?.ok || !ev.evidence) {
      return { ok: false, error: formatSelfImproveError(ev.error || 'no evidence') };
    }

    let result = await finalizeExternalOutcome(sessionId, ev.evidence);

    if (!result.ok && (result.status === 404 || isMissingOutcomeError(result.error))) {
      result = await applySignalViaLegacyEndpoints(sessionId, ev.evidence, model_id);
    }

    return result;
  } catch (e) {
    return { ok: false, error: formatSelfImproveError(e?.message || String(e)) };
  }
}
